import { ApiClient } from "../config/axiosConfig";
import { IPaginatedResponse } from "../models/Pagination";
import {
  ICreatePartNumberTransfer,
  IFilterWarehouse,
  IPartNumberDtoInsert,
  IPartNumberDtoResponse,
  IPartNumberDtoUpdate,
  IPartNumberGroupRestock,
  IPartNumberTransfer,
  IPartNumberTransferDetail,
  IPartNumberTransferExport,
  IPartNumberTransferExportResponse,
  IReturnSlipWarehouse,
  IReturnSlipWarehouseDetail,
  IWarehouseExport,
} from "../models/Warehouse";
import {
  IUpdateStatusExportTransfer,
  IWarehouseExportTransfer,
  IWarehouseLocationCreate,
  IWarehouseLocationUpdate,
} from "./../models/Warehouse";

export const warehouseService = {
  importWarehouse: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await ApiClient.POST("/warehouse/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res;
  },
  getWarehouse: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    code?: string;
    location?: string;
    awb?: string;
    partNumber?: string;
    projectCode?: string;
    project?: string;
    stock?: number;
    date?: string;
  }) => {
    const res = await ApiClient.GET("/warehouse", params);
    return res;
  },
  insertWarehouse: async (data: IPartNumberDtoInsert[]) => {
    const res = await ApiClient.POST("/warehouse/insert", data);
    return res;
  },
  updateWarehouse: async (id: number, data: IPartNumberDtoUpdate) => {
    const res = await ApiClient.PUT(`/warehouse/${id}`, data);
    return res;
  },

  getWarehouseByPartNumber: async (
    partNumber: string,
  ): Promise<IPartNumberDtoResponse[]> => {
    const res = await ApiClient.GET(
      `warehouse/search?partNumber=${partNumber}`,
    );
    return res;
  },
  getWarehouseByProject: async (project: string) => {
    const res = await ApiClient.GET(`warehouse/project/${project}`);
    return res;
  },

  getWarehouseLocation: async () => {
    const res = await ApiClient.GET("/warehouse-location");
    return res;
  },

  getWarehouseLocationById: async (id: number) => {
    const res = await ApiClient.GET(`/warehouse-location/${id}`);
    return res;
  },
  createWarehouseLocation: async (data: IWarehouseLocationCreate) => {
    const res = await ApiClient.POST("/warehouse-location/add-location", data);
    return res;
  },
  editWarehouseLocation: async (data: IWarehouseLocationUpdate) => {
    const res = await ApiClient.PUT(
      `/warehouse-location/${data.id}/edit-location`,
      data,
    );
    return res;
  },
  getAllWarehouseGroup: async (params: IFilterWarehouse) => {
    const res = await ApiClient.GET("/part-number-group", params);
    return res;
  },
  getWarehouseGroupById: async (id: number) => {
    const res = await ApiClient.GET(`/part-number-group/${id}`);
    return res;
  },
  requestQcForWarehouseGroup: async (id: number, priority: string) => {
    const res = await ApiClient.PATCH(`/part-number-group/${id}/request-qc`, {
      priority: priority,
    });
    return res;
  },

  importExportWarehouse: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await ApiClient.POST("/warehouse/export/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res;
  },
  exportWarehouse: async (data: IWarehouseExport) => {
    const res = await ApiClient.POST("/warehouse/export", data);
    return res;
  },
  reverseExportWarehouse: async (id: number, reason: string) => {
    const res = await ApiClient.POST(`/warehouse/export/${id}/reverse`, {
      reason: reason,
    });
    return res;
  },
  searchWarehouseExports: async (params: IFilterWarehouse) => {
    const res = await ApiClient.GET("/warehouse/export", params);
    return res;
  },

  exportWarehouseTransfer: async (data: IWarehouseExportTransfer) => {
    const res = await ApiClient.POST("/warehouse/export-transfer", data);
    return res;
  },
  importExportWarehouseTransfer: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await ApiClient.POST(
      "/warehouse/export-transfer/import",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return res;
  },
  getWarehouseExportTransfer: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    transferId?: number;
  }) => {
    const res = await ApiClient.GET("/warehouse/export-transfer", params);
    return res;
  },
  updateWarehouseExportTransfer: async (data: IUpdateStatusExportTransfer) => {
    const res = await ApiClient.PATCH(
      `/warehouse/export-transfer/${data.id}/status`,
      { status: data.status },
    );
    return res;
  },
  transferWarehouse: async (data: ICreatePartNumberTransfer) => {
    const res = await ApiClient.POST("/warehouse/transfer", data);
    return res;
  },
  getTransferWarehouse: async (params: {
    page: number;
    limit: number;
    search?: string;
  }) => {
    const res = await ApiClient.GET<IPaginatedResponse<IPartNumberTransfer>>(
      "/warehouse/transfer",
      params,
    );
    return res;
  },
  importTransferWarehouse: async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const res = await ApiClient.POST("/warehouse/transfer/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return res;
  },
  getTransferWarehouseDetail: async (id: number) => {
    const res = await ApiClient.GET<IPartNumberTransferDetail>(
      `/warehouse/transfer/${id}`,
    );
    return res;
  },
  completeTransferWarehouseDetail: async (id: number) => {
    const res = await ApiClient.PATCH<IPartNumberTransferDetail>(
      `/warehouse/transfer/${id}/complete`,
    );
    return res;
  },
  exportFromTransfer: (id: number, body: IPartNumberTransferExport) => {
    const res = ApiClient.POST<IPartNumberTransferExportResponse>(
      `/warehouse/transfer/${id}/export`,
      body,
    );
    return res;
  },
  getReturnSlipWarehouse: (param: IFilterWarehouse) => {
    const res = ApiClient.GET<IPaginatedResponse<IReturnSlipWarehouse>>(
      "/warehouse/return-slips",
      param,
    );
    return res;
  },
  getReturnSlipWarehouseDetail: (id: number) => {
    const res = ApiClient.GET<IReturnSlipWarehouseDetail>(
      `/warehouse/return-slips/${id}`,
    );
    return res;
  },
  acceptReturnWarehouse: (id: number, locationId: number) => {
    const res = ApiClient.POST(`/warehouse/return-slips/${id}/return`, {
      locationId,
    });
    return res;
  },
  getReturnPartNumberByGroup: (id: number) => {
    const res = ApiClient.GET<IPartNumberGroupRestock>(
      `/part-number-group/${id}/get-restock-list`,
    );
    return res;
  },
  returnPartNumberByGroup: (
    ngLocationsByItem: Record<number, number | null>,
  ) => {
    const res = ApiClient.POST<number>(
      `/part-number-restock/restock-warehouse`,
      ngLocationsByItem,
    );
    return res;
  },
};
